import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import MediaCard from '@/components/base/MediaCard';
import EmptyState from '@/components/base/EmptyState';
import { getHot, searchMedia } from '@/lib/media';

const HOT_KEYWORDS = ['科幻', '悬疑', '古装', '爱情', '真人秀', '2024', '中国'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(initial);

  useEffect(() => {
    setKeyword(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    document.title = keyword ? `搜索“${keyword}” - 神马视频` : '影视搜索 - 神马视频';
  }, [keyword]);

  const results = useMemo(() => searchMedia(keyword), [keyword]);
  const hotRecommend = getHot(4);

  const updateKeyword = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    updateKeyword(keyword);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-100">
      <Header />

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="rounded-lg bg-gradient-to-br from-primary-900 to-primary-700 p-5 md:p-8">
          <h1 className="text-xl md:text-3xl font-bold text-background-50">影视搜索</h1>
          <p className="mt-2 text-sm md:text-base text-background-200">
            输入影视名称或标签关键词，快速查找你想要的影视内容
          </p>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => updateKeyword(event.target.value)}
                placeholder="例如：科幻、悬疑、2024"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-300">热门搜索：</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => updateKeyword(word)}
                className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 text-xs hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </div>

        {keyword.trim() ? (
          <>
            <div className="mt-6 mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-foreground-600">
                关键词 <span className="font-semibold text-primary-700">“{keyword}”</span> 共找到{' '}
                <span className="font-semibold text-accent-600">{results.length}</span> 个结果
              </p>
              <button
                type="button"
                onClick={() => updateKeyword('')}
                className="text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                清空关键词
              </button>
            </div>

            {results.length > 0 ? (
              <div className="flex flex-col gap-3">
                {results.map((item) => (
                  <MediaCard key={item.id} media={item} variant="list" />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到相关影视"
                description={`没有与“${keyword}”匹配的内容，换个关键词试试，或看看下面的热门推荐。`}
                action={
                  <button
                    type="button"
                    onClick={() => updateKeyword('')}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清空关键词
                  </button>
                }
              />
            )}
          </>
        ) : (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-2-line"
              title="开始你的影视搜索"
              description="输入影视名称、主演或标签关键词即可开始查找，支持模糊匹配。"
            />
          </div>
        )}

        <section className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
              <i className="ri-fire-line"></i>
            </span>
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">热门影视推荐</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {hotRecommend.map((item) => (
              <MediaCard key={item.id} media={item} />
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-600 cursor-pointer"
            >
              浏览全部影视分类
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}